import { DEFAULT_ERROR_MESSAGE, FAILURE_HEADER } from 'common/config';
import { COMPONENT_REGISTRY } from 'common/config/routes.config';
import { MessageType } from 'common/enums';
import {
    ComponentRegistry,
    ComponentRegistryItemWithSubRoutes,
    FeatureActionsList,
    FeaturesAndActions,
    FeaturesList
} from 'common/models/roles-and-access/roleAccess.model';
import { appStateActions } from 'common/redux/app-state';
import { AppDispatch } from 'common/redux/core/store.config';
import { FeaturesService } from 'common/redux/user/features.service';
import { Dispatch, SetStateAction } from 'react';

export const getFeaturesAndActionsData = async (
    dispatch: AppDispatch,
    setRolesData: Dispatch<
        SetStateAction<{
            featuresActions: FeatureActionsList;
            featuresHierarchy: ComponentRegistryItemWithSubRoutes[];
        }>
    >
): Promise<void> => {
    dispatch({
        type: appStateActions.showLoader.type,
        payload: true
    });
    const featuresData = await FeaturesService.getFeatures();
    if (featuresData?.hasErrors && featuresData.errors && featuresData?.errors.length > 0) {
        dispatch({
            type: appStateActions.showLoader.type,
            payload: false
        });
        /** need to show failed pop up here */
        dispatch({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: DEFAULT_ERROR_MESSAGE,
                type: MessageType.Error
            }
        });
    }
    if (featuresData?.value && !featuresData?.hasErrors) {
        const routeData = createRoutesData(featuresData?.value);
        routeData && setRolesData(routeData);
        dispatch({
            type: appStateActions.showLoader.type,
            payload: false
        });
    }
};

/** this is a recursive function to convert nested subRoutes of each ele into array  */
const createData = (
    SubRegistry: ComponentRegistry,
    subFeatures: FeaturesList
): ComponentRegistryItemWithSubRoutes[] => {
    const routeData = subFeatures
        .map((sf) => {
            if (SubRegistry[sf.featureKey]) {
                if (sf.subFeatures.length > 0 && SubRegistry[sf.featureKey].subRoutes) {
                    const data = createData(
                        SubRegistry[sf.featureKey].subRoutes as ComponentRegistry,
                        sf.subFeatures
                    );
                    if (data.length !== 0) {
                        return { ...SubRegistry[sf.featureKey], subRoutes: data };
                    }
                    return { ...SubRegistry[sf.featureKey], subRoutes: undefined };
                }
                return { ...SubRegistry[sf.featureKey], subRoutes: undefined };
            }
            return null;
        })
        .filter((ele) => ele !== null);
    return routeData.filter(
        (ele) => ele !== null
    ) as unknown as ComponentRegistryItemWithSubRoutes[];
};

/**this data will create routes data using api response which then will be used to create Router routes and sidebar menu */
const createRoutesData = (
    featuresData: FeaturesAndActions
): {
    featuresActions: FeatureActionsList;
    featuresHierarchy: ComponentRegistryItemWithSubRoutes[];
} | null => {
    if (featuresData.featuresHierarchy && COMPONENT_REGISTRY) {
        /** sort as per registry */
        const sortedHierarchyData = Object.keys(COMPONENT_REGISTRY)
            .map((key) => {
                const data = featuresData.featuresHierarchy.find((ele) => ele.featureKey === key);
                return data;
            })
            .filter((ele) => ele !== undefined);

        /**create routes data as requuired  */
        const hierarchyData = sortedHierarchyData
            .filter((ele) => ele !== undefined)
            .map((ele) => {
                if (ele && COMPONENT_REGISTRY[ele.featureKey]) {
                    if (
                        ele.subFeatures.length > 0 &&
                        COMPONENT_REGISTRY[ele.featureKey].subRoutes
                    ) {
                        const data = createData(
                            COMPONENT_REGISTRY[ele.featureKey].subRoutes as ComponentRegistry,
                            ele.subFeatures
                        );
                        if (data.length !== 0) {
                            return {
                                ...COMPONENT_REGISTRY[ele.featureKey],
                                subRoutes: data
                            };
                        }
                        return COMPONENT_REGISTRY[ele.featureKey];
                    }
                    return COMPONENT_REGISTRY[ele.featureKey];
                }
                return null;
            })
            .filter((ele) => ele !== null);

        return {
            featuresActions: featuresData.featuresActions,
            featuresHierarchy: hierarchyData as unknown as ComponentRegistryItemWithSubRoutes[]
        };
    }
    return null;
};
